import type { ReactNode } from "react";
import type { ReadingItem } from "@edufadder/shared-types";
import { Chip, colors, fonts, spacing } from "@edufadder/ui";
import * as s from "../styles.js";

/** Only plain https links from the catalogues are ever rendered as links. */
export function safeHttps(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === "https:" ? u.toString() : null;
  } catch {
    return null;
  }
}

export function ReadingBadges({ item }: { item: ReadingItem }): JSX.Element {
  return (
    <div style={{ display: "flex", gap: spacing(2), flexWrap: "wrap", margin: `${spacing(2)} 0` }}>
      {item.curator_pick && <Chip tone="green">Curator pick</Chip>}
      <Chip tone="blue">Free to read</Chip>
      {item.top_1_percent ? (
        <Chip tone="amber">Top 1% most cited in its field</Chip>
      ) : item.top_10_percent ? (
        <Chip tone="amber">Top 10% most cited in its field</Chip>
      ) : null}
      {item.work_type === "review" && <Chip>Review of many studies</Chip>}
      {item.in_eric ? <Chip>Peer-reviewed · in ERIC</Chip> : item.peer_reviewed && <Chip>Journal article</Chip>}
      {item.wwc_reviewed && <Chip tone="green">Reviewed by What Works Clearinghouse</Chip>}
      {item.in_web_of_science && <Chip>Indexed in Web of Science</Chip>}
    </div>
  );
}

export function ReadingCard({ item, footer }: { item: ReadingItem; footer?: ReactNode }): JSX.Element {
  const full = safeHttps(item.full_text_url);
  const landing = safeHttps(item.landing_url);
  return (
    <li style={{ listStyle: "none", borderTop: `1px solid ${colors.border}`, padding: `${spacing(4)} 0` }}>
      <div style={{ fontFamily: fonts.serif, fontSize: 18, fontWeight: 700, lineHeight: 1.35, color: colors.ink }}>{item.title}</div>
      <div style={{ ...s.mutedText, fontSize: 14, marginTop: 4 }}>
        {[item.authors, item.venue, item.year ? String(item.year) : ""].filter(Boolean).join(" · ")}
      </div>
      <ReadingBadges item={item} />
      <div style={{ display: "flex", gap: spacing(4), flexWrap: "wrap", alignItems: "center", fontSize: 14 }}>
        {full && (
          <a href={full} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700 }}>
            Read the full text →
          </a>
        )}
        {landing && landing !== full && (
          <a href={landing} target="_blank" rel="noopener noreferrer">
            {item.doi ? `doi:${item.doi}` : "Publisher page"}
          </a>
        )}
        <span style={s.mutedText}>Cited {item.cited_by_count.toLocaleString()} times</span>
      </div>
      {footer}
    </li>
  );
}

/** What every list says about itself — kept in one place so the wording matches the vetting rule. */
export function HowTheseWereChosen({ sources }: { sources?: string[] }): JSX.Element {
  const from = sources && sources.length > 0 ? sources.join(", ") : "OpenAlex and ERIC";
  return (
    <details style={{ fontSize: 14, color: colors.muted, margin: `${spacing(2)} 0 ${spacing(3)}` }}>
      <summary style={{ cursor: "pointer", fontWeight: 700, color: colors.ink }}>How these articles were chosen</summary>
      <p style={{ lineHeight: 1.6 }}>
        Found automatically in open research catalogues ({from}) and refreshed every week. An article is listed only if it is
        free to read, a peer-reviewed or journal article or review, not retracted, and among the <strong>top 10% most cited</strong>{" "}
        papers of its field and year. The search phrase must appear in its title or abstract.
      </p>
      <p style={{ lineHeight: 1.6 }}>
        Being highly cited means many researchers engaged with a paper — not that it's right. Some of these may argue{" "}
        <em>for</em> the idea. EduFadder's own summary above is the curated part; this list is for going further.
      </p>
    </details>
  );
}
